import { Component } from '@theme/component';
import { onDocumentReady, prefersReducedMotion } from '@theme/utilities';

const PDP_BACKGROUND_VIDEO_SELECTOR = '.media-block__media--video-background[autoplay]';
const BACKGROUND_VIDEO_ROOT_MARGIN = '500px';
const initializedBackgroundVideos = new WeakSet();

/**
 * Bootstraps autoplay for the raw PDP background video path.
 * This supplements native autoplay without affecting other video systems.
 *
 * @param {HTMLVideoElement} video
 */
function bootstrapBackgroundVideoAutoplay(video) {
  if (initializedBackgroundVideos.has(video)) return;

  initializedBackgroundVideos.add(video);

  const abortController = new AbortController();
  const { signal } = abortController;

  const cleanup = () => abortController.abort();
  const tryPlay = () => {
    if (!video.paused) {
      cleanup();
      return;
    }

    let playResult;

    try {
      playResult = video.play();
    } catch {
      return;
    }

    if (playResult && typeof playResult.then === 'function') {
      playResult.then(cleanup).catch(() => {});
    } else if (!video.paused) {
      cleanup();
    }
  };

  video.defaultMuted = true;
  video.muted = true;
  video.playsInline = true;

  video.addEventListener('play', cleanup, { once: true, signal });
  video.addEventListener('loadedmetadata', tryPlay, { signal });
  video.addEventListener('canplay', tryPlay, { signal });

  tryPlay();
}

/**
 * @param {ParentNode} [root]
 */
function initBackgroundVideoAutoplay(root = document) {
  for (const video of root.querySelectorAll(PDP_BACKGROUND_VIDEO_SELECTOR)) {
    if (video instanceof HTMLVideoElement) {
      bootstrapBackgroundVideoAutoplay(video);
    }
  }
}

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.backgroundVideoLoaded === 'true') return;

    if (prefersReducedMotion()) return;

    if (!('IntersectionObserver' in window)) {
      this.#loadVideo();
      return;
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#intersectionObserver?.disconnect();
        this.#intersectionObserver = undefined;
        this.#loadVideo();
      },
      { rootMargin: BACKGROUND_VIDEO_ROOT_MARGIN }
    );

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = undefined;
  }

  #loadVideo() {
    if (this.dataset.backgroundVideoLoaded === 'true') return;

    const { videoSources, videoElement } = this.refs;

    bootstrapBackgroundVideoAutoplay(videoElement);

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
    this.dataset.backgroundVideoLoaded = 'true';
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}

onDocumentReady(() => {
  initBackgroundVideoAutoplay();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initBackgroundVideoAutoplay(event.target);
  }
});
